/**
 * Concise rules, scoring and the premium-square legend. Used as the lobby's
 * "How to play" disclosure and as a dialog from the game menu / help button.
 */
import { BINGO_BONUS, MIN_BAG_FOR_EXCHANGE, RACK_SIZE } from '@wordloom/engine';
import { useUiStore } from '../state/uiStore';
import { Modal } from './Modal';
import './HowToPlay.css';

const LEGEND = [
  { kind: 'tw', label: '3W', text: 'Triple word' },
  { kind: 'dw', label: '2W', text: 'Double word' },
  { kind: 'tl', label: '3L', text: 'Triple letter' },
  { kind: 'dl', label: '2L', text: 'Double letter' },
] as const;

export function HowToPlayContent() {
  return (
    <div className="howto">
      <section className="howto__section">
        <h3>The idea</h3>
        <p>
          Take turns weaving words onto the board, crossword-style. Each of you holds {RACK_SIZE} letter tiles; the
          highest score when the game ends wins.
        </p>
      </section>
      <section className="howto__section">
        <h3>On your turn</h3>
        <ul>
          <li>
            <strong>Play</strong> tiles in a single row or column. They must connect to tiles already on the board — the
            very first word must cover the <span className="howto__star">★</span>.
          </li>
          <li>Every word your tiles form (including crossings) must be in the dictionary.</li>
          <li>
            Or <strong>exchange</strong> any tiles with the bag (while it holds at least {MIN_BAG_FOR_EXCHANGE}), or{' '}
            <strong>pass</strong>. Both use your turn.
          </li>
          <li>
            A <strong>blank</strong> tile can be any letter you choose, but is worth 0 points.
          </li>
        </ul>
      </section>
      <section className="howto__section">
        <h3>Scoring</h3>
        <p>
          A word scores the sum of its letters. Premium squares count only on the turn they are covered: letter premiums
          multiply one tile, word premiums the whole word (the ★ is a double word). Use all {RACK_SIZE} tiles in one move
          for a <strong>+{BINGO_BONUS} bingo</strong>.
        </p>
        <ul className="howto__legend" aria-label="Premium squares">
          {LEGEND.map((l) => (
            <li key={l.kind}>
              <span className={`howto__swatch howto__swatch--${l.kind}`} aria-hidden="true">
                {l.label}
              </span>
              {l.text}
            </li>
          ))}
          <li>
            <span className="howto__swatch howto__swatch--dw" aria-hidden="true">
              ★
            </span>
            Start (double word)
          </li>
        </ul>
      </section>
      <section className="howto__section">
        <h3>The end</h3>
        <p>
          The game ends when someone plays their last tile with the bag empty — they gain the value of the other
          player’s remaining tiles, which the other player loses — or after too many passes in a row, when each player
          loses the value of their own rack. In timed games, running out of time passes your turn.
        </p>
      </section>
    </div>
  );
}

export function HowToPlayDialog() {
  const open = useUiStore((s) => s.dialog === 'howto');
  const closeDialog = useUiStore((s) => s.closeDialog);
  const openDialog = useUiStore((s) => s.openDialog);
  return (
    <Modal
      open={open}
      onClose={closeDialog}
      title="How to play"
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={() => openDialog('shortcuts')}>
            Keyboard shortcuts
          </button>
          <button type="button" className="btn btn--primary" onClick={closeDialog}>
            Got it
          </button>
        </>
      }
    >
      <HowToPlayContent />
    </Modal>
  );
}
